/**
 * @author Tomas Frinta
 * @version 
 * 1.00
 * 1.01 (16.12.2019) null nahrazeno undefined
 * 1.02 (05.02.2020) add vraci uzel s pridanou polozkou
 */

import { Node } from "./Node";
import { Pool } from "./Pool";


export class NodeList<T> {
    private _nodePool: Pool<Node<T>>;

    get first(): T { return (this._firstNode != undefined ? this._firstNode.data : undefined); }

    get last(): T { return (this._lastNode != undefined ? this._lastNode.data : undefined); }

    get firstNode(): Node<T> { return this._firstNode; }
    private _firstNode: Node<T>;

    get lastNode(): Node<T> { return this._lastNode; }
    private _lastNode: Node<T>;

    /**
     * (RO) Pocet polozek v seznamu
     */
    get size(): number { return this._itemCnt; }
    private _itemCnt: number;

    /**
     * (RO) True, pokud je seznam prazdny
     */
    get empty(): boolean { return this._itemCnt == 0; }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor() {
        this._nodePool = new Pool<Node<T>>(undefined, 0, true, (): Node<T> => {
            return new Node<T>();
        }, this);

        this._firstNode = undefined;
        this._lastNode = undefined;
        this._itemCnt = 0;
    }

    //-------------------------------------------------------------------------
    /**
     * Vyprazdneni seznamu
     */
    //-------------------------------------------------------------------------
    clear(): NodeList<T> {
        let node = this._firstNode;

        while (node != undefined) {
            this._nodePool.returnItem(node);
            node = node.next;
        }

        this._firstNode = undefined;
        this._lastNode = undefined;
        this._itemCnt = 0;

        return this;
    }

    //-------------------------------------------------------------------------
    /**
     * Pridani polozky do seznamu
     * @param item
     * @param id
     */
    //-------------------------------------------------------------------------
    add(item: T, id?: number): Node<T> {
        let node = this._nodePool.getItem();

        if (id == undefined || id >= this._itemCnt) {
            // Pridat jako posledni polozku
            node.addToChain(this._lastNode);
        }
        else {
            if (id == 0) {
                // Pridat jako prvni polozku
                node.addToChain(this._firstNode, false);
            }
            else {
                // Najit polozku, za kterou se ma nova polozka pridat
                node.addToChain(this.getItemById(id - 1));
            }
        }

        if (node.prev == undefined)
            this._firstNode = node;

        if (node.next == undefined)
            this._lastNode = node;

        node.data = item;
        this._itemCnt++;

        return node;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    removeById(id: number): T {
        let node = this.getItemById(id);

        if (node == undefined)
            return undefined;

        return this.removeByNode(node);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    removeByNode(node: Node<T>): T {
        if (this._firstNode == node)
            this._firstNode = node.next;

        if (this._lastNode == node)
            this._lastNode = node.prev;

        node.removeFromChain();
        this._nodePool.returnItem(node);
        this._itemCnt--;

        return node.data;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    getItemById(id: number): Node<T> {
        if (id < 0 || id >= this._itemCnt)
            return undefined;

        let node: Node<T>;

        if (id < (this._itemCnt >> 1)) {
            // Hledani od prvniho uzlu
            node = this._firstNode;

            while (id-- != 0)
                node = node.next;
        }
        else {
            // Hledani od posledniho uzlu
            let _id = this._itemCnt;

            node = this._lastNode;

            while (--_id != id)
                node = node.prev;
        }

        return node;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    forEach(clb: (item: T, node?: Node<T>, id?: number) => void, clbCtx?: any): NodeList<T> {
        let node = this._firstNode;
        let i = 0;

        while (node != undefined) {
            let nextNode = node.next;

            clb.call(clbCtx, node.data, node, i++);
            node = nextNode;
        }

        return this;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    every(clb: (item: T, node: Node<T>) => boolean, clbCtx?: any): NodeList<T> {
        let node = this._firstNode;

        while (node != undefined) {
            let nextNode = node.next;

            if (!clb.call(clbCtx, node.data, node))
                break;

            node = nextNode;
        }

        return this;
    }
}